import { onMounted, onUnmounted, ref, Ref, watch, nextTick } from 'vue'

/**
 * Composable for managing focus trap in modals (WCAG 2.2 compliant)
 *
 * @param modalRef - Ref to the modal element
 * @param options - Configuration options
 * @returns Object with methods to control focus trap
 */
export const useFocusTrap = (
  modalRef: Ref<HTMLElement | null>,
  options?: {
    initialFocusSelector?: string
    onEscape?: () => void
    autoFocus?: boolean
    isActive?: Ref<boolean>
  }
) => {
  const previousActiveElement = ref<HTMLElement | null>(null)
  const isTrapping = ref(false)
  const {
    initialFocusSelector,
    onEscape = () => {},
    autoFocus = true,
    isActive,
  } = options || {}

  // Get all focusable elements within the modal
  const getFocusableElements = (): HTMLElement[] => {
    if (!modalRef.value) return []

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ')

    const elements = modalRef.value.querySelectorAll<HTMLElement>(focusableSelectors)
    return Array.from(elements).filter(el => {
      return el.offsetParent !== null && !el.hasAttribute('aria-hidden')
    })
  }

  // Focus the first focusable element or a specific element
  const focusFirstElement = () => {
    if (!modalRef.value) return

    let elementToFocus: HTMLElement | null = null

    // Try to focus the initial focus selector if provided
    if (initialFocusSelector) {
      elementToFocus = modalRef.value.querySelector<HTMLElement>(initialFocusSelector)
    }

    // If no initial selector or not found, focus first focusable element
    if (!elementToFocus) {
      const focusableElements = getFocusableElements()
      elementToFocus = focusableElements[0] || null
    }

    if (elementToFocus) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        elementToFocus?.focus()
      }, 100)
    }
  }

  // Handle Tab key to trap focus
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!modalRef.value || !isTrapping.value) return

    // Handle Escape key
    if (event.key === 'Escape') {
      event.preventDefault()
      onEscape()
      return
    }

    // Only trap focus for Tab key
    if (event.key !== 'Tab') return

    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // If Shift + Tab on first element, focus last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    // If Tab on last element, focus first
    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
      return
    }
  }

  // Activate the focus trap
  const activate = async () => {
    if (isTrapping.value) return

    // Store the currently focused element to restore later
    previousActiveElement.value = document.activeElement as HTMLElement

    isTrapping.value = true

    // Wait for DOM to be ready
    await nextTick()

    // Auto-focus if enabled
    if (autoFocus) {
      focusFirstElement()
    }

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown)
  }

  // Deactivate the focus trap and restore previous focus
  const deactivate = () => {
    if (!isTrapping.value) return

    isTrapping.value = false
    document.removeEventListener('keydown', handleKeyDown)

    // Restore focus to the element that was focused before the modal opened
    if (previousActiveElement.value && previousActiveElement.value.focus) {
      setTimeout(() => {
        previousActiveElement.value?.focus()
      }, 100)
    }
  }

  // Watch for modal ref changes (for v-if modals)
  watch(modalRef, async (newVal, oldVal) => {
    if (newVal && !oldVal) {
      // Modal just appeared
      await activate()
    } else if (!newVal && oldVal) {
      // Modal just disappeared
      deactivate()
    }
  })

  // Watch for isActive changes if provided
  if (isActive) {
    watch(isActive, async (active) => {
      if (active) {
        await activate()
      } else {
        deactivate()
      }
    }, { immediate: true })
  }

  // Set up and tear down
  onMounted(async () => {
    // Only activate on mount if modalRef is already available
    if (modalRef.value) {
      await activate()
    }
  })

  onUnmounted(() => {
    deactivate()
  })

  return {
    activate,
    deactivate,
    focusFirstElement,
    isTrapping,
  }
}
